/* fruvi – Header/Mega-Menü, Karussell-Filter, Box-Finder, PLZ-Check (gemeinsamer Stand) */

(() => {
  const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const STORAGE_KEY = 'fruvi-plz';

  /* ---------- PLZ → Liefertermine ----------
     Quelle 1: Logifresh (get-available-delivery-dates) – Zonen, Vorlauf, Bestellschluss, Feiertage, DHL.
     Quelle 2 (Fallback ohne API-URL): PLZ-Tabelle aus Metaobjekten plz_liefertag bzw. Theme-Einstellung.
     Speicherung nur als Warenkorb-Attribute (ein Termin pro Warenkorb, Appstle und Logifresh lesen „Liefertermin“):
     „Liefertermin“ (Standard ISO YYYY-MM-DD), „Liefertermin_Anzeige“ (z. B. „Mi, 7. Okt.“), „PLZ“. */
  const config = window.fruviConfig || {};
  const DAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const MONTHS_SHORT = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
  const LEGACY_KEYS = ['_Delivery Day', '_Delivery Date', 'Lieferung', 'Liefertag', 'Liefertermin', 'Lieferdatum'];
  const CACHE_MS = 10 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 8000;
  const pad = (n) => String(n).padStart(2, '0');
  const fromIso = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : null;
  };
  const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fmtDE = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  const fmtAppstle = (d, fmt) =>
    String(fmt || 'YYYY-MM-DD')
      .replace('YYYY', String(d.getFullYear()))
      .replace('MM', pad(d.getMonth() + 1))
      .replace('DD', pad(d.getDate()));

  const parseTable = (text) =>
    String(text || '')
      .split(/\n/)
      .map((line) => line.split('|').map((part) => part.trim()))
      .filter(([range, day]) => range && day)
      .map(([range, day]) => {
        const [from, to] = range.split('-').map((n) => parseInt(n, 10));
        return { from, to: Number.isNaN(to) ? from : to, day };
      });

  const table = parseTable(config.plzTable);
  const apiBase = String(config.logifreshApi || '')
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/get-available-delivery-dates$/, '');

  const describeDate = (d, cutoffDays) => {
    const cut = new Date(d);
    cut.setDate(cut.getDate() - cutoffDays);
    return {
      iso: isoOf(d),
      weekday: WEEKDAYS[d.getDay()],
      date: d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' }),
      full: `${WEEKDAYS[d.getDay()]}, ${fmtDE(d)}`,
      cut: apiBase ? '' : cut.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' }),
    };
  };

  const fruviPlz = {
    cutoffDays: Number(config.cutoffDays) || 4,
    source: apiBase ? 'logifresh' : 'table',
    state: { plz: '', status: null, day: null, dates: [], mode: '', ts: 0 },

    lookup(plz) {
      const n = parseInt(plz, 10);
      const hit = table.find((row) => n >= row.from && n <= row.to);
      return hit ? hit.day : null;
    },

    /* Liste aus der Tabelle: die nächsten Termine am Liefertag, frühestens heute + Bestellschluss */
    tableDates(dayName, count = 8) {
      const wanted = String(dayName || '').trim().toLowerCase();
      const target = WEEKDAYS.findIndex((w) => w.toLowerCase() === wanted || (wanted.length >= 2 && w.toLowerCase().startsWith(wanted.slice(0, 2))));
      if (target < 0) return [];
      const out = [];
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() + this.cutoffDays);
      while (out.length < count) {
        if (d.getDay() === target) out.push(isoOf(d));
        d.setDate(d.getDate() + 1);
      }
      return out;
    },

    async fetchLogifresh(plz) {
      const url = `${apiBase}/get-available-delivery-dates?shop_domain=${encodeURIComponent(config.shopDomain || '')}&zip=${encodeURIComponent(plz)}&num_days=62`;
      const once = async () => {
        const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl?.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json();
        } finally {
          if (timer) clearTimeout(timer);
        }
      };
      let json;
      try {
        json = await once();
      } catch (e) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        json = await once();
      }
      return {
        mode: json && json.mode ? String(json.mode) : '',
        dates: (json && Array.isArray(json.dates) ? json.dates : [])
          .map((d) => String(d.delivery_date || '').slice(0, 10))
          .filter((iso) => fromIso(iso)),
      };
    },

    check(plz) {
      // Laufende Anfrage für dieselbe PLZ wiederverwenden (Drawer, Seite und Wiederherstellen fragen gleichzeitig)
      if (this.pending && this.pendingPlz === plz) return this.pending;
      this.pendingPlz = plz;
      this.pending = this.runCheck(plz).finally(() => {
        this.pending = null;
        this.pendingPlz = null;
      });
      return this.pending;
    },

    async runCheck(plz) {
      if (!/^\d{5}$/.test(plz)) {
        this.set({ plz, status: 'bad', day: null, dates: [], mode: '', ts: 0 });
        return this.state;
      }
      if (this.source === 'table') {
        const day = this.lookup(plz);
        this.set({ plz, status: day ? 'ok' : 'no', day, dates: day ? this.tableDates(day) : [], mode: '', ts: Date.now() });
        return this.state;
      }
      this.set({ ...this.state, plz, status: 'loading' });
      try {
        const result = await this.fetchLogifresh(plz);
        const weekdays = [...new Set(result.dates.map((iso) => WEEKDAYS[fromIso(iso).getDay()]))];
        const day = weekdays.length ? weekdays.join(', ').replace(/, ([^,]*)$/, ' und $1') : null;
        this.set({ plz, status: result.dates.length ? 'ok' : 'no', day, dates: result.dates, mode: result.mode, ts: Date.now() });
      } catch (e) {
        this.set({ plz, status: 'error', day: null, dates: [], mode: '', ts: 0 });
      }
      return this.state;
    },

    set(state) {
      this.state = state;
      try {
        if (state.status === 'ok' || state.status === 'no') localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, source: this.source }));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('fruvi:plz', { detail: state }));
    },

    restore() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!saved || !saved.plz) return;
        this.state = saved;
        // Terminliste veraltet oder aus anderer Quelle → still neu laden
        if (saved.source !== this.source || Date.now() - (saved.ts || 0) > CACHE_MS) {
          this.state = { ...saved, status: null, dates: [] };
          setTimeout(() => this.check(saved.plz), 0);
        }
      } catch (e) {}
    },

    /* Die nächsten wählbaren Liefertermine (nur künftige) */
    nextDates(count = 5) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return (this.state.dates || [])
        .map(fromIso)
        .filter((d) => d && d >= today)
        .slice(0, count)
        .map((d) => describeDate(d, this.cutoffDays));
    },

    /* Datum aus Warenkorb/Positionen lesen: ISO, sonst Tag-Monat-Jahr (30-09-2026, 30.09.2026, „Mi 30.09.2026“) */
    parseDate(str) {
      const value = String(str || '');
      const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(value);
      if (iso) return fromIso(iso[0]) ? iso[0] : null;
      const fmt = String(config.deliveryFormat || '');
      const m = /(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})/.exec(value);
      if (!m) return null;
      let [day, month] = [Number(m[1]), Number(m[2])];
      if (fmt.startsWith('MM')) [day, month] = [month, day];
      const d = new Date(Number(m[3]), month - 1, day, 12);
      return Number.isNaN(d.getTime()) || d.getDate() !== day ? null : isoOf(d);
    },

    longDate(iso) {
      const d = fromIso(iso);
      return d ? `${WEEKDAYS[d.getDay()]}, ${fmtDE(d)}` : '';
    },

    /* „Mi, 7. Okt.“ */
    shortDate(iso) {
      const d = fromIso(iso);
      if (!d) return '';
      return `${DAYS_SHORT[d.getDay()]}, ${d.getDate()}. ${MONTHS_SHORT[d.getMonth()]}`;
    },

    /* Bestellschluss als „Sa, 3.10.“ */
    cutShort(iso) {
      const d = fromIso(iso);
      if (!d) return '';
      d.setDate(d.getDate() - this.cutoffDays);
      return `${DAYS_SHORT[d.getDay()]}, ${d.getDate()}.${d.getMonth() + 1}.`;
    },

    /* „Mittwochs und freitags“ aus den Terminen */
    weekdayPhrase() {
      const days = [...new Set((this.state.dates || []).map((iso) => fromIso(iso)?.getDay()).filter((n) => n !== undefined))].sort(
        (a, b) => ((a + 6) % 7) - ((b + 6) % 7)
      );
      const words = days.map((n) => WEEKDAYS[n].toLowerCase() + 's');
      const text = words.length > 1 ? `${words.slice(0, -1).join(', ')} und ${words[words.length - 1]}` : words[0] || '';
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    cartUrl() {
      return window.routes?.cart_url || '/cart';
    },

    async post(path, body) {
      const response = await fetch(`${this.cartUrl()}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },

    /* Termin + PLZ als Warenkorb-Attribute speichern (ein Termin pro Warenkorb) */
    async saveDelivery(iso, plz) {
      const d = fromIso(iso);
      if (!d) return null;
      // Andere Bausteine (z. B. Produktseite) verwerfen ihren gemerkten Warenkorb-Stand
      document.dispatchEvent(new CustomEvent('fruvi:cart-attrs'));
      return this.post('update.js', {
        attributes: {
          [config.deliveryAttribute || 'Liefertermin']: fmtAppstle(d, config.deliveryFormat),
          Liefertermin_Anzeige: this.shortDate(iso),
          PLZ: plz || '',
          'Liefer-PLZ': plz || '',
        },
      });
    },

    async clearDelivery() {
      document.dispatchEvent(new CustomEvent('fruvi:cart-attrs'));
      return this.post('update.js', {
        attributes: { [config.deliveryAttribute || 'Liefertermin']: '', Liefertermin_Anzeige: '' },
      });
    },

    /* Alte Termin-Eigenschaften an Positionen entfernen (frühere Produktseite / fruvi-liefertermin-App).
       Liefert den gefundenen Termin (ISO) zurück, damit er ins Attribut übernommen werden kann. */
    async stripLegacyProperties() {
      const keys = LEGACY_KEYS.map((k) => k.toLowerCase());
      const cart = await fetch(`${this.cartUrl()}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      let found = null;
      let changed = false;
      for (const [index, item] of (cart.items || []).entries()) {
        const props = item.properties || {};
        const legacy = Object.keys(props).filter((k) => keys.includes(k.toLowerCase()) && String(props[k] ?? '').trim() !== '');
        if (!legacy.length) continue;
        // Reihenfolge wie LEGACY_KEYS: eigene Felder der Produktseite vor der alten Liefertermin-App
        legacy
          .sort((a, b) => keys.indexOf(a.toLowerCase()) - keys.indexOf(b.toLowerCase()))
          .forEach((k) => (found = found || this.parseDate(props[k])));
        // Leere Werte entfernt Shopify aus den Eigenschaften
        const rest = Object.fromEntries(Object.entries(props).map(([k, v]) => [k, legacy.includes(k) ? '' : v]));
        await this.post('change.js', { line: index + 1, quantity: item.quantity, properties: rest });
        changed = true;
      }
      return { found, changed };
    },
  };

  fruviPlz.restore();
  window.fruviPlz = fruviPlz;

  class FruviPlz extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-fr-plz-input]');
      this.field = this.querySelector('[data-fr-plz-field]');
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 5);
        this.field.classList.remove('is-ok', 'is-error');
        this.toggleMessages(null);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          fruviPlz.check(this.input.value);
        }
      });
      this.querySelector('[data-fr-plz-check]').addEventListener('click', () => fruviPlz.check(this.input.value));
      this.onPlz = (event) => this.render(event.detail);
      document.addEventListener('fruvi:plz', this.onPlz);
      if (fruviPlz.state.plz) this.render(fruviPlz.state);
    }

    disconnectedCallback() {
      document.removeEventListener('fruvi:plz', this.onPlz);
    }

    render(state) {
      if (document.activeElement !== this.input) this.input.value = state.plz;
      this.field.classList.toggle('is-ok', state.status === 'ok');
      this.field.classList.toggle('is-error', state.status === 'no' || state.status === 'bad' || state.status === 'error');
      this.querySelector('[data-fr-plz-check]').disabled = state.status === 'loading';
      this.querySelectorAll('[data-fr-plz-value]').forEach((el) => (el.textContent = state.plz));
      this.querySelectorAll('[data-fr-plz-day]').forEach((el) => (el.textContent = state.day || ''));
      this.querySelectorAll('[data-fr-plz-daylabel]').forEach((el) => (el.textContent = String(state.day || '').includes(' und ') ? 'Deine Liefertage:' : 'Dein Liefertag:'));
      this.querySelectorAll('[data-fr-plz-dhl]').forEach((el) => (el.hidden = state.mode !== 'dhl'));
      /* Konkrete Termine aus Logifresh (bzw. aus der PLZ-Tabelle, wenn keine API eingetragen ist) */
      const list = this.querySelector('[data-fr-plz-datelist]');
      if (list) {
        list.innerHTML = '';
        (state.dates || [])
          .filter((iso) => { const d = fromIso(iso); const t = new Date(); t.setHours(0, 0, 0, 0); return d && d >= t; })
          .slice(0, Number(this.dataset.count) || 4)
          .forEach((iso) => {
            const li = document.createElement('li');
            li.textContent = fruviPlz.shortDate(iso);
            list.appendChild(li);
          });
      }
      this.toggleMessages(state.status);
    }

    toggleMessages(status) {
      this.querySelector('[data-fr-plz-ok]').hidden = status !== 'ok';
      this.querySelector('[data-fr-plz-no]').hidden = status !== 'no';
      this.querySelector('[data-fr-plz-bad]').hidden = status !== 'bad';
      this.querySelector('[data-fr-plz-loading]').hidden = status !== 'loading';
      this.querySelector('[data-fr-plz-error]').hidden = status !== 'error';
    }

    focus() {
      this.input.focus();
    }
  }

  if (!customElements.get('fruvi-plz')) customElements.define('fruvi-plz', FruviPlz);

  /* ---------- Header ---------- */
  class FruviHeader extends HTMLElement {
    connectedCallback() {
      this.burger = this.querySelector('[data-fr-burger]');
      this.mobnav = this.querySelector('[data-fr-mobnav]');
      this.megaToggle = this.querySelector('[data-fr-mega-toggle]');
      this.mega = this.querySelector('[data-fr-mega]');

      this.burger?.addEventListener('click', () => {
        const open = this.mobnav.hidden;
        this.mobnav.hidden = !open;
        this.burger.setAttribute('aria-expanded', String(open));
      });

      const acc = this.querySelector('[data-fr-mobacc]');
      const panel = this.querySelector('[data-fr-mobpanel]');
      acc?.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        acc.setAttribute('aria-expanded', String(open));
        acc.querySelector('.fr-mobnav__icon').textContent = open ? '–' : '+';
      });

      if (this.megaToggle && this.mega) {
        this.megaToggle.addEventListener('click', () => this.setMega(this.mega.hidden));
        this.megaToggle.addEventListener('mouseenter', () => this.setMega(true));
        this.addEventListener('mouseleave', () => this.setMega(false));
        document.addEventListener('keyup', (event) => {
          if (event.key === 'Escape') this.setMega(false);
        });
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.setMega(false);
        });
      }
    }

    setMega(open) {
      this.mega.hidden = !open;
      this.megaToggle.setAttribute('aria-expanded', String(open));
    }
  }

  if (!customElements.get('fruvi-header')) customElements.define('fruvi-header', FruviHeader);

  /* ---------- Box-Karussell: Filter + Pfeile ---------- */
  class FruviCarousel extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-fr-track]');
      if (!track) return;
      const prev = this.querySelector('[data-fr-prev]');
      const next = this.querySelector('[data-fr-next]');
      const arrows = this.querySelector('[data-fr-arrows]');
      // Schrittweite: eine Karte inkl. Abstand
      const step = () => {
        const card = [...track.children].find((c) => !c.hidden);
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : 310;
      };
      const go = (dir) => track.scrollBy({ left: dir * step(), behavior: 'smooth' });
      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      track.addEventListener('keydown', (event) => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight') go(1);
        else if (event.key === 'ArrowLeft') go(-1);
        else return;
        event.preventDefault();
      });
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (arrows) arrows.hidden = max <= 2;
      };
      track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      sync();
      const chips = this.querySelectorAll('[data-fr-filter]');
      chips.forEach((chip) =>
        chip.addEventListener('click', () => {
          const filter = chip.dataset.frFilter;
          chips.forEach((c) => {
            c.classList.toggle('is-active', c === chip);
            c.setAttribute('aria-pressed', String(c === chip));
          });
          track.querySelectorAll('[data-kind]').forEach((card) => {
            card.hidden = filter !== 'alle' && card.dataset.kind !== filter;
          });
          track.scrollTo({ left: 0 });
          sync();
        })
      );
    }
  }

  if (!customElements.get('fruvi-carousel')) customElements.define('fruvi-carousel', FruviCarousel);

  /* ---------- Ankündigungsleiste: Vorteile wechseln mobil automatisch, Wischen/Tippen schaltet weiter ---------- */
  class FruviRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-rotator-item]'));
      this.index = 0;
      this.setAttribute('data-ready', '');
      if (this.items.length < 2) {
        if (this.items[0]) this.items[0].classList.add('is-active');
        return;
      }
      this.items[0].classList.add('is-active');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.delay = Math.max(2, Number(this.dataset.interval) || 4) * 1000;

      let startX = null;
      this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          startX = null;
          this.show(this.index + (dx > 30 ? -1 : 1));
          this.restart();
        },
        { passive: true }
      );
      document.addEventListener('visibilitychange', () => this.restart());
      this.mobile.addEventListener('change', () => this.restart());
      this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    restart() {
      clearInterval(this.timer);
      if (!this.mobile.matches || this.reduced.matches || document.hidden) return;
      this.timer = setInterval(() => this.show(this.index + 1), this.delay);
    }

    show(next) {
      const n = this.items.length;
      const target = ((next % n) + n) % n;
      if (target === this.index) return;
      const old = this.items[this.index];
      old.classList.remove('is-active');
      old.classList.add('is-leaving');
      setTimeout(() => old.classList.remove('is-leaving'), 400);
      this.index = target;
      this.items[target].classList.add('is-active');
    }
  }

  if (!customElements.get('fruvi-rotator')) customElements.define('fruvi-rotator', FruviRotator);

  /* ---------- Firmen: Mengen-Rechner ---------- */
  class FruviB2bCalc extends HTMLElement {
    connectedCallback() {
      this.people = 20;
      this.days = 1;
      this.querySelector('[data-calc-inc]').addEventListener('click', () => this.setPeople(this.people + 5));
      this.querySelector('[data-calc-dec]').addEventListener('click', () => this.setPeople(this.people - 5));
      this.querySelectorAll('[data-calc-days]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.days = Number(btn.dataset.calcDays);
          this.update();
        })
      );
      this.update();
    }

    setPeople(n) {
      this.people = Math.min(300, Math.max(5, n));
      this.update();
    }

    update() {
      const d = this.dataset;
      const perPerson = (Number(d.perPerson) || 150) / 1000;
      const kgS = Number(d.sKg) || 5;
      const kgM = Number(d.mKg) || 8;
      const kgL = Number(d.lKg) || 12;
      // Menge pro Lieferung: Personen × Richtwert × 5 Arbeitstage, verteilt auf die Lieferungen
      const kg = (this.people * perPerson * 5) / this.days;
      let rec;
      if (kg <= kgS * 1.2) rec = `${Math.max(1, Math.ceil(kg / kgS))} × ${d.sName}`;
      else if (kg <= kgM * 1.25) rec = `1 × ${d.mName}`;
      else rec = `${Math.ceil(kg / kgL)} × ${d.lName}`;
      this.querySelector('[data-calc-people]').textContent = this.people;
      this.querySelector('[data-calc-rec]').textContent = rec;
      this.querySelector('[data-calc-sub]').textContent = `ca. ${Math.round(kg)} kg pro Lieferung, ${this.days}× pro Woche`;
      this.querySelector('[data-calc-dec]').disabled = this.people <= 5;
      this.querySelector('[data-calc-inc]').disabled = this.people >= 300;
      this.querySelectorAll('[data-calc-days]').forEach((btn) => {
        const on = Number(btn.dataset.calcDays) === this.days;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', String(on));
      });
    }
  }

  if (!customElements.get('fruvi-b2b-calc')) customElements.define('fruvi-b2b-calc', FruviB2bCalc);

  /* ---------- Box-Finder ---------- */
  class FruviFinder extends HTMLElement {
    connectedCallback() {
      this.taste = Number(this.dataset.taste || 2);
      this.people = Number(this.dataset.people || 0);
      this.querySelectorAll('[data-fr-taste]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.taste = Number(btn.dataset.frTaste);
          this.update();
        })
      );
      this.querySelectorAll('[data-fr-people]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.people = Number(btn.dataset.frPeople);
          this.update();
        })
      );
      this.update();
    }

    get recId() {
      if (this.taste === 0) return 'obst';
      if (this.taste === 1) return 'gemuese';
      if (this.taste === 3) return 'echt';
      if (this.taste === 4) return 'apfel';
      return this.people === 2 ? 'family' : 'mix';
    }

    update() {
      const mark = (selector, key, value) =>
        this.querySelectorAll(selector).forEach((btn) => {
          const active = Number(btn.dataset[key]) === value;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', String(active));
        });
      mark('[data-fr-taste]', 'frTaste', this.taste);
      mark('[data-fr-people]', 'frPeople', this.people);

      this.querySelectorAll('[data-finder-rec]').forEach((rec) => {
        const show = rec.dataset.finderRec === this.recId;
        rec.hidden = !show;
        if (!show) return;
        const sizes = rec.querySelectorAll('[data-size]');
        const index = Math.min(this.people, sizes.length - 1);
        sizes.forEach((size, i) => (size.hidden = i !== index));
      });
    }
  }

  if (!customElements.get('fruvi-finder')) customElements.define('fruvi-finder', FruviFinder);

  /* ---------- FAQ: immer nur eine offen (Fallback für Browser ohne <details name>) ---------- */
  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open) return;
      const list = item.closest('[data-fr-faq]');
      if (!list) return;
      list.querySelectorAll('details[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    },
    true
  );
})();
